import React from 'react';
import { User, Car, Building2 } from 'lucide-react';

/**
 * Unified audience navigation (Customers / Drivers / Businesses).
 * One rounded container holding three equally-weighted, colour-themed tabs.
 * Links, wording and colours are unchanged — this is presentation only.
 */
const TABS = [
  {
    key: 'customers',
    href: '/for-customers',
    label: 'Customers',
    Icon: User,
    testid: 'nav-customers',
    text: 'text-[#1B5E20]',
    tint: 'bg-green-50/70 hover:bg-green-100',
    activeTint: 'bg-green-100',
    ring: 'ring-[#2E7D32]/60',
  },
  {
    key: 'drivers',
    href: '/for-drivers',
    label: 'Drivers',
    Icon: Car,
    testid: 'nav-drivers',
    text: 'text-[#E65100]',
    tint: 'bg-orange-50/70 hover:bg-orange-100',
    activeTint: 'bg-orange-100',
    ring: 'ring-[#F57C00]/60',
  },
  {
    key: 'businesses',
    href: '/for-businesses',
    label: 'Businesses',
    Icon: Building2,
    testid: 'nav-businesses',
    text: 'text-[#0D47A1]',
    tint: 'bg-blue-50/70 hover:bg-blue-100',
    activeTint: 'bg-blue-100',
    ring: 'ring-[#1565C0]/60',
  },
];

export const AudienceTabs = ({ active, size = 'md', className = '' }) => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const current = active || TABS.find((t) => t.href === path)?.key;
  const compact = size === 'sm';

  return (
    <div
      className={`inline-flex items-center gap-1 p-1 rounded-full bg-gray-50 border border-gray-200/80 shadow-[0_1px_3px_rgba(16,24,40,0.06)] box-border max-w-full overflow-hidden ${className}`}
      data-testid="audience-tabs"
    >
      {TABS.map(({ key, href, label, Icon, testid, text, tint, activeTint, ring }) => {
        const isActive = current === key;
        return (
          <a
            key={key}
            href={href}
            data-testid={isActive ? `${testid}-active` : testid}
            aria-current={isActive ? 'page' : undefined}
            className={`inline-flex items-center justify-center gap-1.5 rounded-full font-bold
              transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.97]
              ${compact ? 'flex-1 min-w-0 px-1.5 py-2 text-[11.5px] sm:text-[13px] gap-1' : 'flex-none px-4 lg:px-5 py-2.5 text-base whitespace-nowrap'}
              ${text} ${isActive ? `${activeTint} ring-2 ${ring}` : `${tint} ring-1 ring-transparent`}`}
          >
            <Icon className={`flex-shrink-0 ${compact ? 'w-[15px] h-[15px]' : 'w-[18px] h-[18px]'}`} strokeWidth={2.5} aria-hidden="true" />
            <span className={compact ? 'truncate' : ''}>{label}</span>
          </a>
        );
      })}
    </div>
  );
};

export default AudienceTabs;
